import loginBg from '@/assets/figma/login-bg.svg';
import circleArrow from '@/assets/figma/login-circle-arrow.svg';
import logoLetters from '@/assets/figma/login-logo-letters.svg';
import logoSymbol from '@/assets/figma/login-logo-symbol.svg';
import type { SocialProvider } from '@/entities/auth/api/auth.api';
import { SOCIAL_PROVIDER_LABEL } from '@/entities/auth/social/socialRedirect';
import { BetaHelpText } from '@/pages/login/components/BetaHelpText';
import { LoginForm } from '@/pages/login/components/LoginForm';
import { LoginNoticeDialog } from '@/pages/login/components/LoginNoticeDialog';
import { SocialLoginButtons } from '@/pages/login/components/SocialLoginButtons';
import { toastApi } from '@/shared/utils/toast';
import { getRouteApi } from '@tanstack/react-router';
import { useEffect, useState } from 'react';

const route = getRouteApi('/login');

/** 고객센터 안내 (Figma 833:7447). 카카오톡 채널 주소는 환경변수가 있을 때만 링크로 건다 */
const SUPPORT = {
  hours: '평일 10:00-17:00',
  phone: '1600-3884',
  email: 'admin@rudalink.com',
  kakaoUrl: import.meta.env.VITE_KAKAO_CHANNEL_URL,
  homepageUrl: import.meta.env.VITE_HOMEPAGE_URL ?? 'https://rudalink.com',
};

/** M-03 X1 · X4 — SNS 로그인 실패 팝업 */
type SocialNotice =
  { kind: 'failed'; provider?: SocialProvider } | { kind: 'signup' };

const socialNoticeText = (notice: SocialNotice) =>
  notice.kind === 'signup'
    ? {
        title: '회원가입은 앱에서 할 수 있어요',
        body: '루다링크 앱에서 가입한 뒤 같은 계정으로 로그인해주세요.',
      }
    : {
        title: '로그인할 수 없어요',
        body: `${notice.provider ? `${SOCIAL_PROVIDER_LABEL[notice.provider]} ` : ''}로그인이 취소됐거나 연결에 실패했어요. 잠시 후 다시 시도해주세요.`,
      };

/**
 * M-01 로그인 — 로그아웃 상태의 유일한 진입점.
 * 데스크톱은 좌 브랜드 / 우 폼을 반씩, 모바일(<1024)은 폼만 한 단으로 둔다.
 */
export const LoginPage = () => {
  // SNS 인증 진행 중에는 다른 버튼과 이메일 폼을 비활성화한다
  const [pendingProvider, setPendingProvider] = useState<string | null>(null);
  const [socialNotice, setSocialNotice] = useState<SocialNotice | null>(null);

  const search = route.useSearch();
  const navigate = route.useNavigate();

  useEffect(() => {
    // 새로고침 때 다시 뜨지 않도록 처리한 reason은 주소에서 지운다
    switch (search.reason) {
      case 'expired':
        // X7 — 세션 만료로 들어왔을 때 상단 토스트 3초
        toastApi.info('세션이 만료되었어요. 다시 로그인해주세요.', 3);
        break;
      case 'social_failed':
        // X1 — SNS 인증 취소 · 실패 (콜백 화면에서 돌아옴). 비활성 계정도 같은 문구를 쓴다
        setSocialNotice({ kind: 'failed', provider: search.provider });
        break;
      case 'signup_required':
        // X4 — 앱에서 해당 SNS로 가입 · 연결한 이력이 없음
        setSocialNotice({ kind: 'signup' });
        break;
      default:
        return;
    }
    void navigate({
      search: (prev) => ({ ...prev, reason: undefined, provider: undefined }),
      replace: true,
    });
  }, [search.reason, search.provider, navigate]);

  const notice = socialNotice && socialNoticeText(socialNotice);

  return (
    <div className="flex min-h-dvh bg-[#f5f7f9]">
      {/* 왼쪽 브랜드 패널 (Figma 833:7458) — 모바일(<1024)에서는 숨긴다 */}
      <aside className="hidden w-1/2 shrink-0 p-6 lg:flex">
        <div className="relative flex-1 overflow-hidden rounded-3xl bg-[#d1e7fe]">
          <img
            src={loginBg}
            alt=""
            width={672}
            height={852}
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div className="absolute top-8 left-8 flex w-40 flex-col gap-6">
            <img src={logoSymbol} alt="" width={64} height={64} />
            <img src={logoLetters} alt="Rudalink" width={160} height={32} />
          </div>
          <p className="absolute right-8 bottom-8 left-8 text-[40px] leading-[52px] font-extrabold tracking-[-1px] text-white">
            처방 등록부터
            <br />
            정산 확인까지,
            <br />
            루다링크에서 한 번에.
          </p>
        </div>
      </aside>

      <main className="relative flex flex-1 flex-col items-center px-4 pt-6 pb-10 lg:px-[120px] lg:pt-[124px]">
        <BetaHelpText className="mb-8 w-full max-w-[480px] lg:absolute lg:top-6 lg:right-6 lg:mb-0 lg:w-auto" />

        <div className="flex w-full max-w-[480px] flex-1 flex-col justify-center gap-10 pb-16">
          <LoginForm
            disabled={pendingProvider !== null}
            redirectTo={search.redirect}
          />
          <SocialLoginButtons
            pendingProvider={pendingProvider}
            onPendingChange={setPendingProvider}
            onError={(provider) =>
              setSocialNotice({ kind: 'failed', provider })
            }
            redirectTo={search.redirect}
          />
        </div>

        <footer className="flex w-full max-w-[480px] flex-wrap items-center whitespace-nowrap lg:flex-nowrap gap-x-6 gap-y-1 text-[13px] leading-5 tracking-[-0.065px] text-[#9ea2ae]">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 lg:flex-nowrap">
            <span>{SUPPORT.hours}</span>
            <a href={`tel:${SUPPORT.phone.replace(/-/g, '')}`}>
              {SUPPORT.phone}
            </a>
            <a href={`mailto:${SUPPORT.email}`}>{SUPPORT.email}</a>
            <ExternalLink href={SUPPORT.kakaoUrl}>카카오톡</ExternalLink>
          </div>
          <ExternalLink href={SUPPORT.homepageUrl}>홈페이지</ExternalLink>
        </footer>
      </main>

      <LoginNoticeDialog
        notice={notice}
        onClose={() => setSocialNotice(null)}
      />
    </div>
  );
};

/** 바깥 링크 + 원형 화살표 아이콘. 주소가 없으면 글자만 둔다 */
const ExternalLink = ({
  href,
  children,
}: {
  href?: string;
  children: string;
}) => {
  const content = (
    <>
      {children}
      <img src={circleArrow} alt="" width={14} height={14} />
    </>
  );
  return href ? (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-0.5"
    >
      {content}
    </a>
  ) : (
    <span className="flex items-center gap-0.5">{content}</span>
  );
};
